// ===== AUTO-GENERATED API SLICES FROM SERVICE CONFIGURATION =====
// src/store/api/generatedApis.js
import { baseApi } from "./baseApi.js";
import { serviceRegistry } from "../../utils/serviceRegistry.js";
import { APP_CONFIG } from "../../constants/app.js";

/**
 * Service slice factory - creates RTK Query slices from service configuration
 */
export const createServiceSlice = (serviceKey) => {
  const serviceConfig = serviceRegistry.services[serviceKey];

  if (!serviceConfig) {
    throw new Error(`Service ${serviceKey} not found in registry`);
  }

  const endpoints = {};

  // Generate endpoints from service configuration
  Object.entries(serviceConfig.endpoints).forEach(
    ([endpointKey, endpointConfig]) => {
      const isQuery = endpointConfig.method === "GET";

      if (isQuery) {
        // Create query endpoint
        endpoints[`get${endpointKey}`] = (builder) =>
          builder.query({
            query: (params = {}) => {
              const { pathParams = {}, ...queryParams } = params;

              return {
                url: serviceRegistry.buildUrl(
                  serviceKey,
                  endpointKey,
                  pathParams,
                  queryParams
                ),
                method: endpointConfig.method,
              };
            },

            providesTags: (result, error, arg) => {
              const tags = endpointConfig.cache?.tags || [];

              // Add specific item tags for list endpoints
              if (endpointKey === "LIST" && result?.data) {
                const itemTags = result.data.map((item) => ({
                  type: serviceConfig.resource,
                  id: item.id,
                }));
                return [...tags, ...itemTags];
              }

              // Add specific item tag for individual items
              if (
                endpointConfig.params?.includes("id") &&
                arg?.pathParams?.id
              ) {
                tags.push({
                  type: serviceConfig.resource,
                  id: arg.pathParams.id,
                });
              }

              return tags;
            },

            // Cache configuration
            keepUnusedDataFor:
              endpointConfig.cache?.ttl || APP_CONFIG.DEFAULT_CACHE_TTL,

            // Polling configuration
            ...(endpointConfig.polling?.enabled && {
              pollingInterval: endpointConfig.polling.interval,
            }),

            // Transform response
            transformResponse: (response, meta, arg) => {
              // Handle paginated responses
              if (
                response &&
                typeof response === "object" &&
                "data" in response &&
                "pagination" in response
              ) {
                return response;
              }

              // Handle array responses (wrap in data structure)
              if (Array.isArray(response)) {
                return { data: response };
              }

              return response;
            },

            // Error handling
            transformErrorResponse: (response) => ({
              status: response.status,
              message:
                response.data?.message ||
                `Failed to fetch ${endpointConfig.description}`,
              code: response.data?.code,
            }),
          });
      } else {
        // Create mutation endpoint
        endpoints[endpointKey.toLowerCase()] = (builder) =>
          builder.mutation({
            query: (params = {}) => {
              const { pathParams = {}, body, ...queryParams } = params;

              return {
                url: serviceRegistry.buildUrl(
                  serviceKey,
                  endpointKey,
                  pathParams,
                  queryParams
                ),
                method: endpointConfig.method,
                body,
                ...(endpointConfig.responseType === "blob" && {
                  responseHandler: "blob",
                }),
              };
            },

            // Cache invalidation
            invalidatesTags: (result, error, arg) => {
              if (error) return [];

              const tags = endpointConfig.invalidates || [];

              // Add specific item tags for update/delete operations
              if (
                ["PUT", "PATCH", "DELETE"].includes(endpointConfig.method) &&
                arg?.pathParams?.id
              ) {
                tags.push({
                  type: serviceConfig.resource,
                  id: arg.pathParams.id,
                });
              }

              return tags;
            },

            // Optimistic updates
            ...(endpointConfig.optimistic && {
              onQueryStarted: async (
                args,
                { dispatch, queryFulfilled, ...api }
              ) => {
                let patchResult;

                try {
                  // Implement optimistic update logic based on operation type
                  if (
                    endpointConfig.method === "DELETE" &&
                    args.pathParams?.id
                  ) {
                    // Optimistically remove item from list
                    patchResult = dispatch(
                      api.endpoints[
                        `get${serviceKey === "USERS" ? "LIST" : "LIST"}`
                      ].util.updateQueryData(undefined, (draft) => {
                        if (draft?.data) {
                          draft.data = draft.data.filter(
                            (item) => item.id !== args.pathParams.id
                          );
                        }
                      })
                    );
                  } else if (
                    endpointConfig.method === "PUT" &&
                    args.pathParams?.id
                  ) {
                    // Optimistically update item
                    patchResult = dispatch(
                      api.endpoints[
                        `get${serviceKey === "USERS" ? "LIST" : "LIST"}`
                      ].util.updateQueryData(undefined, (draft) => {
                        if (draft?.data) {
                          const index = draft.data.findIndex(
                            (item) => item.id === args.pathParams.id
                          );
                          if (index !== -1) {
                            Object.assign(draft.data[index], args.body);
                          }
                        }
                      })
                    );
                  }

                  // Wait for the actual request to complete
                  const { data } = await queryFulfilled;

                  // Show success notification
                  dispatch({
                    type: "ui/addNotification",
                    payload: {
                      type: "success",
                      message: `${endpointConfig.description} completed successfully`,
                    },
                  });
                } catch (error) {
                  // Rollback optimistic update on error
                  if (patchResult) {
                    patchResult.undo();
                  }

                  // Show error notification
                  dispatch({
                    type: "ui/addNotification",
                    payload: {
                      type: "error",
                      message:
                        error.data?.message ||
                        `Failed to ${endpointConfig.description}`,
                    },
                  });
                }
              },
            }),
          });
      }
    }
  );

  // Inject endpoints into base API
  return baseApi.injectEndpoints({
    endpoints: (builder) => {
      const builtEndpoints = {};
      Object.entries(endpoints).forEach(([name, endpointBuilder]) => {
        builtEndpoints[name] = endpointBuilder(builder);
      });
      return builtEndpoints;
    },
    overrideExisting: false,
  });
};

// ===== GENERATE API SLICES FOR ALL SERVICES =====

// Authentication API
export const authApi = createServiceSlice("AUTH");

// User Management API
export const usersApi = createServiceSlice("USERS");

// Dashboard API
// export const dashboardApi = createServiceSlice("DASHBOARD");

// Reports API
// export const reportsApi = createServiceSlice("REPORTS");

// ===== EXPORT HOOKS WITH DESCRIPTIVE NAMES =====

// Authentication hooks
export const {
  useLoginMutation,
  useLogoutMutation,
  useGetRefreshTokenMutation: useRefreshTokenMutation,
  useGetCurrentUserQuery,
  useGetForgotPasswordMutation: useForgotPasswordMutation,
  useGetResetPasswordMutation: useResetPasswordMutation,
  useGetChangePasswordMutation: useChangePasswordMutation,
} = authApi;

// User management hooks
export const {
  useGetLISTQuery: useGetUsersQuery,
  useGetGET_BY_IDQuery: useGetUserQuery,
  useCreateMutation: useCreateUserMutation,
  useUpdateMutation: useUpdateUserMutation,
  useDeleteMutation: useDeleteUserMutation,
  useBulk_updateMutation: useBulkUpdateUsersMutation,
  useGetEXPORTQuery: useExportUsersQuery,
  useImportMutation: useImportUsersMutation,
} = usersApi;

// Dashboard hooks
// export const {
//   useGetSTATSQuery: useGetDashboardStatsQuery,
//   useGetRECENT_ACTIVITIESQuery: useGetRecentActivitiesQuery,
//   useGetSYSTEM_HEALTHQuery: useGetSystemHealthQuery,
//   useGetUSER_GROWTHQuery: useGetUserGrowthDataQuery,
//   useGetTOP_PERFORMERSQuery: useGetTopPerformersQuery,
//   useExport_analyticsMutation: useExportAnalyticsMutation,
// } = dashboardApi;

// Reports hooks
// export const {
//   useGetLISTQuery: useGetReportsQuery,
//   useGetGET_BY_IDQuery: useGetReportByIdQuery,
//   useGenerateMutation: useGenerateReportMutation,
//   useExportMutation: useExportReportMutation,
//   useDeleteMutation: useDeleteReportMutation,
//   useScheduleMutation: useScheduleReportMutation,
// } = reportsApi;

// ===== ENHANCED HOOKS WITH ADDITIONAL FUNCTIONALITY =====

/**
 * Enhanced users query with additional helpers
 */
// export const useUsersWithPagination = (params = {}) => {
//   const result = useGetUsersQuery(params);

//   return {
//     ...result,
//     users: result.data?.data || [],
//     pagination: result.data?.pagination || {},
//     hasNextPage:
//       result.data?.pagination?.page < result.data?.pagination?.totalPages,
//     hasPreviousPage: result.data?.pagination?.page > 1,
//     isEmpty:
//       !result.isLoading &&
//       (!result.data?.data || result.data.data.length === 0),
//   };
// };

/**
 * Enhanced dashboard stats with real-time updates
 */
// export const useDashboardStatsWithPolling = (enabled = true) => {
//   const result = useGetDashboardStatsQuery(undefined, {
//     pollingInterval: enabled ? APP_CONFIG.DASHBOARD_POLLING_INTERVAL : 0,
//   });

//   return {
//     ...result,
//     stats: result.data || {},
//     isRealTime: enabled,
//   };
// };

/**
 * Enhanced notifications with unread count
 */
// export const useNotificationsWithCount = () => {
//   const notifications = useGetNotificationsQuery();
//   const unreadCount = useGetUnreadNotificationCountQuery();

//   return {
//     notifications: notifications.data?.data || [],
//     unreadCount: unreadCount.data?.count || 0,
//     isLoading: notifications.isLoading || unreadCount.isLoading,
//     error: notifications.error || unreadCount.error,
//     refetch: () => {
//       notifications.refetch();
//       unreadCount.refetch();
//     },
//   };
// };

// ===== UTILITY FUNCTIONS =====

/**
 * Prefetch data for better UX
 */
// export const prefetchUserData = (dispatch, userId) => {
//   dispatch(
//     usersApi.util.prefetch("getGET_BY_ID", { pathParams: { id: userId } })
//   );
// };

// /**
//  * Invalidate all user-related data
//  */
// export const invalidateUserData = (dispatch) => {
//   dispatch(usersApi.util.invalidateTags(["User"]));
// };

// /**
//  * Clear all API cache
//  */
// export const clearAllCache = (dispatch) => {
//   dispatch(baseApi.util.resetApiState());
// };

// ===== DEVELOPMENT UTILITIES =====
if (APP_CONFIG.IS_DEVELOPMENT) {
  // Make APIs available globally for debugging
  window.authApi = authApi;
  window.usersApi = usersApi;
  // window.dashboardApi = dashboardApi;
  // window.reportsApi = reportsApi;

  console.log("🔧 Generated APIs available globally for debugging");
}
